import React, { useState, useEffect } from 'react';
import { HelpCircle, ArrowRight, X, AlertTriangle } from 'lucide-react';

interface TutorialRecoveryProps {
    currentStep: string;
}

interface RecoveryHint {
    step: string;
    condition: string;
    hint: string;
    action?: string;
}

/**
 * Composant de récupération du tutoriel
 * Détecte quand le joueur est bloqué et propose des indices contextuels
 */
export const TutorialRecovery: React.FC<TutorialRecoveryProps> = ({
    currentStep
}) => {
    const [isIdle, setIsIdle] = useState(false);
    const [idleTime, setIdleTime] = useState(0);
    const [showHelpButton, setShowHelpButton] = useState(false);
    const [showHintModal, setShowHintModal] = useState(false);
    const [lastActivity, setLastActivity] = useState(Date.now());

    // Indices de récupération par étape
    const recoveryHints: RecoveryHint[] = [
        {
            step: 'MESSENGER_PIXEL',
            condition: 'Aucun message reçu depuis 2 minutes',
            hint: 'Pixel devrait vous contacter automatiquement. Si aucun message n\'apparaît, vérifiez l\'icône Messenger dans la barre des tâches.',
            action: 'Cliquez sur l\'icône de messagerie pour ouvrir la conversation avec Pixel.'
        },
        {
            step: 'DRAG_CASE_FILE',
            condition: 'Dossier non glissé sur le tableau depuis 3 minutes',
            hint: 'Vous devez glisser le dossier CASE_FILE_01_AV sur le tableau d\'enquête pour commencer l\'analyse.',
            action: 'Ouvrez l\'Explorateur de fichiers, trouvez le dossier CASE_FILE_01_AV et glissez-le sur le tableau.'
        },
        {
            step: 'FIRST_LINK',
            condition: 'Aucun lien créé depuis 4 minutes',
            hint: 'Créez une corrélation entre deux éléments du dossier Alice en les reliant sur le tableau.',
            action: 'Cliquez sur un élément, maintenez enfoncé, puis glissez vers un autre élément pour créer un lien.'
        },
        {
            step: 'THE_EAR',
            condition: 'L\'Oreille non activée depuis 3 minutes',
            hint: 'Vous devez ouvrir l\'application L\'Oreille pour écouter les interceptions téléphoniques.',
            action: 'Cliquez sur l\'icône L\'Oreille dans la barre des tâches ou le menu Démarrer.'
        },
        {
            step: 'OPTICAL_SUPERPOSITION',
            condition: 'Superposition non utilisée depuis 5 minutes',
            hint: 'Comparez les images d\'Alice en utilisant la superposition optique pour détecter les anomalies.',
            action: 'Glissez deux images sur le tableau. Le système proposera automatiquement la superposition.'
        }
    ];

    // Détection d'inactivité
    useEffect(() => {
        const handleActivity = () => {
            setLastActivity(Date.now());
            setIsIdle(false);
            setIdleTime(0);
        };

        // Écouter les événements d'activité
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
        events.forEach(event => {
            document.addEventListener(event, handleActivity, true);
        });

        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleActivity, true);
            });
        };
    }, []);

    // Timer d'inactivité
    useEffect(() => {
        const interval = setInterval(() => {
            const now = Date.now();
            const timeSinceActivity = now - lastActivity;
            const idleSeconds = Math.floor(timeSinceActivity / 1000);
            
            setIdleTime(idleSeconds);
            
            // Considérer comme inactif après 2 minutes
            if (idleSeconds > 120) {
                setIsIdle(true);
                setShowHelpButton(true);
            } else {
                setIsIdle(false);
                setShowHelpButton(false);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [lastActivity]);

    // Obtenir l'indice pour l'étape actuelle
    const getCurrentHint = (): RecoveryHint | null => {
        return recoveryHints.find(hint => hint.step === currentStep) || null;
    };

    const handleShowHint = () => {
        const hint = getCurrentHint();
        if (hint) {
            setShowHintModal(true);
            // Ne plus envoyer l'indice à Pixel via le Messenger
            // L'indice s'affiche uniquement dans la modal
        }
    };

    const handleExecuteAction = () => {
        const hint = getCurrentHint();
        if (hint?.action) {
            // Déclencher des actions spécifiques selon l'étape
            switch (currentStep) {
                case 'MESSENGER_PIXEL':
                    // Ouvrir automatiquement le messenger
                    window.dispatchEvent(new CustomEvent('OPEN_MESSENGER'));
                    break;
                case 'DRAG_CASE_FILE':
                    // Ouvrir l'explorateur de fichiers
                    window.dispatchEvent(new CustomEvent('OPEN_FILE_EXPLORER'));
                    break;
                case 'THE_EAR':
                    // Ouvrir L'Oreille
                    window.dispatchEvent(new CustomEvent('OPEN_THE_EAR'));
                    break;
                default:
                    break;
            }
        }
        setShowHintModal(false);
    };

    const currentHint = getCurrentHint();

    return (
        <>
            {/* Bouton d'aide flottant */}
            {showHelpButton && currentHint && (
                <div className="fixed bottom-6 left-6 z-50 animate-in slide-in-from-left duration-500">
                    <button
                        onClick={handleShowHint}
                        className="flex items-center gap-3 px-4 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg shadow-lg transition-all duration-300 animate-pulse"
                    >
                        <HelpCircle className="w-5 h-5" />
                        <div className="text-left">
                            <div className="text-sm font-bold">Besoin d'aide ?</div>
                            <div className="text-xs opacity-90">
                                Inactif depuis {Math.floor(idleTime / 60)}m {idleTime % 60}s
                            </div>
                        </div>
                    </button>
                </div>
            )}

            {/* Modal d'indice */}
            {showHintModal && currentHint && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-slate-900 border border-blue-400 rounded-lg p-6 max-w-md w-full mx-4">
                        {/* En-tête */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <HelpCircle className="w-5 h-5 text-blue-400" />
                                <h3 className="text-blue-400 font-mono text-lg">ASSISTANCE PIXEL</h3>
                            </div>
                            <button
                                onClick={() => setShowHintModal(false)}
                                className="text-slate-400 hover:text-white transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Contenu */}
                        <div className="space-y-4">
                            <div className="p-3 bg-slate-800 border border-slate-600 rounded">
                                <div className="text-xs text-slate-400 mb-1">SITUATION DÉTECTÉE:</div>
                                <div className="text-sm text-yellow-400">{currentHint.condition}</div>
                            </div>

                            <div className="p-3 bg-blue-900/20 border border-blue-600 rounded">
                                <div className="text-xs text-blue-400 mb-2">INDICE DE PIXEL:</div>
                                <div className="text-sm text-white">{currentHint.hint}</div>
                            </div>

                            {currentHint.action && (
                                <div className="p-3 bg-green-900/20 border border-green-600 rounded">
                                    <div className="text-xs text-green-400 mb-2">ACTION RECOMMANDÉE:</div>
                                    <div className="text-sm text-white mb-3">{currentHint.action}</div>
                                    <button
                                        onClick={handleExecuteAction}
                                        className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-500 transition-colors text-sm"
                                    >
                                        <ArrowRight className="w-4 h-4" />
                                        Exécuter l'action
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Boutons */}
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setShowHintModal(false)}
                                className="flex-1 px-4 py-2 bg-slate-700 text-slate-300 rounded hover:bg-slate-600 transition-colors"
                            >
                                Fermer
                            </button>
                            <button
                                onClick={() => {
                                    setShowHintModal(false);
                                    setShowHelpButton(false);
                                    setLastActivity(Date.now()); // Reset timer
                                }}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 transition-colors"
                            >
                                Compris
                            </button>
                        </div>

                        {/* Note */}
                        <div className="mt-4 p-2 bg-slate-800 border border-slate-700 rounded">
                            <div className="text-xs text-slate-400 flex items-center gap-2">
                                <AlertTriangle className="w-3 h-3" />
                                Ce système d'aide se déclenche automatiquement en cas d'inactivité prolongée.
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};